// Loading the map's data for the visible time range, merged into GPU-ready binary attributes.

import { useEffect, useMemo, useState } from "react";
import { eventGroup, GROUP_RGB } from "../lib/colors";
import {
  loadAreas, loadGedGrid, loadGedYear, loadNamed, type Areas, type Conflicts, type GedGrid, type GedYear, type Meta,
  type NamedEvents,
} from "../lib/data";

export const DETAIL_SPAN = 30; // years; above this, UCDP events are shown as a per-year grid

/** How long after it ends an event stays visible on the map, fading out (years). */
export function fadeWindow(span: number): number {
  return Math.min(12, Math.max(4 / 365.25, span / 110));
}

export interface PointSet {
  n: number;
  positions: Float32Array; // x,y interleaved
  radii: Float32Array;
  colors: Uint8Array; // rgba
  filter: Float32Array; // t0,t1 interleaved
  conflict: Int32Array;
  kind: Uint8Array;
  lookup: (i: number) => { set: "named" | "ged" | "grid"; ref: unknown; k: number };
}

function overlaps(a0: number, a1: number, b0: number, b1: number) {
  return a0 < b1 && b0 < a1;
}

export function useNamedEvents(meta: Meta | null, a: number, b: number): NamedEvents[] {
  const [chunks, setChunks] = useState<NamedEvents[]>([]);
  const want = useMemo(() => (meta ? meta.chunks.named.filter((c) => overlaps(c.t0, c.t1, a - 30, b + 1)) : []), [meta, Math.floor(a / 10), Math.floor(b / 10)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let live = true;
    Promise.all(want.map(loadNamed)).then((x) => live && setChunks(x)).catch(console.error);
    return () => { live = false; };
  }, [want]);
  return chunks;
}

export function useGed(meta: Meta | null, t: number, span: number): { years: GedYear[]; grid: GedGrid | null } {
  const detailed = span <= DETAIL_SPAN;
  const w = fadeWindow(span);
  const y0 = Math.floor(t - w - 0.25), y1 = Math.floor(t + 0.25);
  const want = useMemo(() => {
    if (!meta || !detailed) return [];
    return meta.chunks.ged.filter((c) => c.year >= y0 && c.year <= y1);
  }, [meta, detailed, y0, y1]);
  const [years, setYears] = useState<GedYear[]>([]);
  const [grid, setGrid] = useState<GedGrid | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all(want.map(loadGedYear)).then((x) => live && setYears(x)).catch(console.error);
    // prefetch the neighbouring years so scrubbing stays smooth
    if (meta && detailed) meta.chunks.ged.filter((c) => c.year === y1 + 1 || c.year === y0 - 1).forEach((c) => loadGedYear(c).catch(() => {}));
    return () => { live = false; };
  }, [want]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!meta || detailed || grid) return;
    let live = true;
    loadGedGrid(meta.chunks.ged_grid).then((g) => live && setGrid(g)).catch(console.error);
    return () => { live = false; };
  }, [meta, detailed, grid]);
  return { years: detailed ? years : [], grid: detailed ? null : grid };
}

export function useAreas(meta: Meta | null, a: number, b: number): Areas[] {
  const want = useMemo(() => (meta ? meta.chunks.areas.filter((c) => overlaps(c.t0, c.t1, a - 5, b + 1)) : []), [meta, Math.floor(a / 10), Math.floor(b / 10)]); // eslint-disable-line react-hooks/exhaustive-deps
  const [chunks, setChunks] = useState<Areas[]>([]);
  useEffect(() => {
    let live = true;
    Promise.all(want.map(loadAreas)).then((x) => live && setChunks(x)).catch(console.error);
    return () => { live = false; };
  }, [want]);
  return chunks;
}

// --- binary attribute builders -----------------------------------------------------------------

const ALPHA_DIM = 70;

function colorInto(out: Uint8Array, k: number, grp: number, alpha: number) {
  const c = GROUP_RGB[grp];
  out[k * 4] = c[0];
  out[k * 4 + 1] = c[1];
  out[k * 4 + 2] = c[2];
  out[k * 4 + 3] = alpha;
}

/** focus: the selected war's family (null when nothing is selected); events outside it are dimmed. */
export function buildNamed(chunks: NamedEvents[], c: Conflicts | null, focus: Uint8Array | null, kinds: boolean[]): PointSet {
  let n = 0;
  for (const ch of chunks) n += ch.n;
  const positions = new Float32Array(n * 2), radii = new Float32Array(n), colors = new Uint8Array(n * 4),
    filter = new Float32Array(n * 2), conflict = new Int32Array(n), kind = new Uint8Array(n);
  const refs: [NamedEvents, number][] = new Array(n);
  let k = 0;
  for (const ch of chunks) {
    for (let j = 0; j < ch.n; j++, k++) {
      positions[k * 2] = ch.lon[j];
      positions[k * 2 + 1] = ch.lat[j];
      const d = ch.d[j], s = ch.s[j];
      radii[k] = d > 0 ? Math.min(15, 3.2 + Math.sqrt(d) / 22) : Math.min(11, 2.6 + Math.log2(1 + s) * 0.95);
      const ci = ch.c[j];
      const root = c && ci >= 0 ? c.r[ci] : -1;
      const ck = c && ci >= 0 ? c.kind[root >= 0 ? root : ci] : null;
      const grp = eventGroup(ch.k[j], ck);
      const hidden = ck != null && !kinds[ck];
      const dim = focus != null && (ci < 0 || !focus[ci]);
      colorInto(colors, k, grp, hidden ? 0 : dim ? ALPHA_DIM : 235);
      filter[k * 2] = ch.t0[j];
      filter[k * 2 + 1] = ch.t1[j];
      conflict[k] = ci;
      kind[k] = ch.k[j];
      refs[k] = [ch, j];
    }
  }
  return { n, positions, radii, colors, filter, conflict, kind, lookup: (i) => ({ set: "named", ref: refs[i][0], k: refs[i][1] }) };
}

export function buildGed(years: GedYear[], c: Conflicts | null, focus: Uint8Array | null, kinds: boolean[]): PointSet {
  let n = 0;
  for (const y of years) n += y.n;
  const positions = new Float32Array(n * 2), radii = new Float32Array(n), colors = new Uint8Array(n * 4),
    filter = new Float32Array(n * 2), conflict = new Int32Array(n), kind = new Uint8Array(n);
  const refs: [GedYear, number][] = new Array(n);
  let k = 0;
  for (const y of years) {
    for (let j = 0; j < y.n; j++, k++) {
      positions[k * 2] = y.lon[j];
      positions[k * 2 + 1] = y.lat[j];
      radii[k] = Math.min(16, 1.7 + Math.sqrt(y.b[j]) * 0.6);
      const ci = y.c[j];
      const root = c && ci >= 0 ? c.r[ci] : -1;
      const ck = c && ci >= 0 ? c.kind[root >= 0 ? root : ci] : null;
      const grp = eventGroup(5 + y.ty[j], ck);
      const hidden = ck != null && !kinds[ck];
      const dim = focus != null && (ci < 0 || !focus[ci]);
      colorInto(colors, k, grp, hidden ? 0 : dim ? 45 : 190);
      filter[k * 2] = y.t0[j];
      filter[k * 2 + 1] = y.t1[j];
      conflict[k] = ci;
      kind[k] = 5 + y.ty[j];
      refs[k] = [y, j];
    }
  }
  return { n, positions, radii, colors, filter, conflict, kind, lookup: (i) => ({ set: "ged", ref: refs[i][0], k: refs[i][1] }) };
}

export function buildGrid(g: GedGrid | null): PointSet {
  const n = g?.n ?? 0;
  const positions = new Float32Array(n * 2), radii = new Float32Array(n), colors = new Uint8Array(n * 4),
    filter = new Float32Array(n * 2), conflict = new Int32Array(n).fill(-1), kind = new Uint8Array(n);
  for (let k = 0; k < n; k++) {
    positions[k * 2] = g!.lon[k];
    positions[k * 2 + 1] = g!.lat[k];
    radii[k] = Math.min(18, 1.5 + Math.sqrt(g!.b[k]) * 0.16);
    const ty = g!.ty[k];
    colorInto(colors, k, ty === 3 ? 2 : 1, 95);
    filter[k * 2] = g!.yr[k];
    filter[k * 2 + 1] = g!.yr[k] + 1;
    kind[k] = 5 + ty;
  }
  return { n, positions, radii, colors, filter, conflict, kind, lookup: (i) => ({ set: "grid", ref: g, k: i }) };
}

export interface AreaPoly { polygon: Float32Array; c: number; t0: number; t1: number; fc: number; area: number; cx: number; cy: number }

/** Footprint rings of top-level conflicts (and the selected conflict), with area and centroid for labels. */
export function buildAreas(chunks: Areas[], c: Conflicts | null, selected: number): AreaPoly[] {
  if (!c) return [];
  const out: AreaPoly[] = [];
  for (const ch of chunks) {
    for (const p of ch.polygons) {
      const ci = ch.c[p.i];
      if (c.p[ci] !== -1 && ci !== selected) continue;
      const r = p.ring;
      let A = 0, cx = 0, cy = 0;
      for (let k = 0, m = r.length / 2; k < m; k++) {
        const x0 = r[k * 2], y0 = r[k * 2 + 1], x1 = r[((k + 1) % m) * 2], y1 = r[((k + 1) % m) * 2 + 1];
        const f = x0 * y1 - x1 * y0;
        A += f;
        cx += (x0 + x1) * f;
        cy += (y0 + y1) * f;
      }
      A /= 2;
      if (Math.abs(A) < 1e-9) continue;
      out.push({ polygon: r, c: ci, t0: ch.t0[p.i], t1: ch.t1[p.i], fc: ch.fc[p.i], area: Math.abs(A), cx: cx / (6 * A), cy: cy / (6 * A) });
    }
  }
  return out;
}
